import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import PageBreadcrumb from '../components/PageBreadcrumb';

const AdminUserDetailPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [activity, setActivity] = useState<any>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [tokenHistory, setTokenHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tokensToAdd, setTokensToAdd] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (username) loadData();
  }, [username]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [details, activityData, stats, history] = await Promise.all([
        adminAPI.getUserDetails(username!),
        adminAPI.getUserActivity(username!, 30).catch(() => null),
        adminAPI.getUserStatistics(username!).catch(() => null),
        adminAPI.getUserTokenHistory(username!).catch(() => []),
      ]);
      setUser(details);
      setActivity(activityData);
      setStatistics(stats);
      setTokenHistory(Array.isArray(history) ? history : []);
    } catch (err: any) {
      console.error('Failed to load user details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTester = async () => {
    if (!user) return;
    const roles: string[] = user.roles || ['user'];
    const isTester = roles.includes('tester');
    const newRoles = isTester
      ? roles.filter((r: string) => r !== 'tester')
      : [...roles.filter((r: string) => r !== 'tester'), 'tester'];
    setSaving(true);
    try {
      await adminAPI.updateUser(username!, { roles: newRoles });
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Rol güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTokens = async () => {
    if (tokensToAdd <= 0) return;
    setSaving(true);
    try {
      await adminAPI.updateUser(username!, { tokens_to_add: tokensToAdd });
      setTokensToAdd(0);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Token eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 dark:border-gray-600 border-t-gray-900 dark:border-t-white" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-red-500">Kullanıcı bulunamadı</div>
      </div>
    );
  }

  const roles: string[] = user.roles || ['user'];
  const isAdmin = roles.includes('admin');
  const isTester = roles.includes('tester');

  // Score distribution from statistics
  const scoreDistribution: { range: string; count: number }[] =
    statistics?.score_distribution || [];
  const projectTypes: Record<string, number> =
    statistics?.project_type_distribution || {};

  // Activity data
  const topFeatures: { feature: string; count: number }[] =
    activity?.top_features || user.most_used_features || [];
  const activityByType: Record<string, number> =
    activity?.activity_by_type || {};
  const dailyActivity: { date: string; count: number }[] =
    activity?.daily_activity || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <PageBreadcrumb
          className="mb-6"
          items={[
            { label: 'Admin', to: '/admin' },
            { label: 'Kullanıcılar', to: '/admin/users' },
            { label: user?.username || username || 'Detay' },
          ]}
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{user.username}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{user.email || 'Email yok'}</p>
            {user.name && <p className="text-xs text-gray-400 dark:text-gray-500">{user.name}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {roles.map((role: string) => (
              <span
                key={role}
                className={`px-3 py-1 text-xs rounded-full font-medium ${
                  role === 'admin'
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    : role === 'tester'
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                {role}
              </span>
            ))}
            {user.primary_country && (
              <span className="px-3 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                {user.primary_country}
              </span>
            )}
          </div>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
          {[
            { label: 'Projeler', value: user.total_projects || 0 },
            { label: 'Kalan Token', value: user.tokens_remaining || 0, color: 'text-green-600 dark:text-green-400' },
            { label: 'Kullanılan', value: user.tokens_used || 0 },
            { label: 'Ort. Puan', value: statistics?.average_score != null ? statistics.average_score.toFixed(1) : (user.average_score != null ? user.average_score.toFixed(1) : '-') },
            { label: 'En Yüksek', value: statistics?.highest_score != null ? statistics.highest_score.toFixed(1) : '-', color: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'En Düşük', value: statistics?.lowest_score != null ? statistics.lowest_score.toFixed(1) : '-', color: 'text-orange-600 dark:text-orange-400' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-4">
              <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{stat.label}</p>
              <p className={`text-xl font-bold ${stat.color || 'text-gray-900 dark:text-white'}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Info Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-4">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Son Giriş</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {user.last_login ? new Date(user.last_login).toLocaleString('tr-TR') : 'Bilinmiyor'}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-4">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Kayıt Tarihi</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-4">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">30 Gün Aktivite</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {user.total_activities || activity?.total_activities || 0} işlem
              {user.avg_tokens_per_day ? ` (${user.avg_tokens_per_day.toFixed(1)} token/gün)` : ''}
            </p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

          {/* Project Types */}
          {Object.keys(projectTypes).length > 0 && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5">
              <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Proje Türleri</h2>
              <div className="space-y-3">
                {Object.entries(projectTypes).map(([type, count]) => {
                  const total = Object.values(projectTypes).reduce((a, b) => a + b, 0);
                  const pct = total > 0 ? ((count as number) / total) * 100 : 0;
                  return (
                    <div key={type}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-700 dark:text-gray-300">{type}</span>
                        <span className="text-gray-500 dark:text-gray-400">{count as number}</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gray-900 dark:bg-white rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Score Distribution */}
          {scoreDistribution.length > 0 && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5">
              <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Puan Dağılımı</h2>
              <div className="space-y-2">
                {scoreDistribution.map((item) => {
                  const maxCount = Math.max(...scoreDistribution.map((d) => d.count), 1);
                  const pct = (item.count / maxCount) * 100;
                  return (
                    <div key={item.range} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400 w-12 shrink-0">{item.range}</span>
                      <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                        <div className="h-full bg-gray-700 dark:bg-gray-400 rounded" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-6 text-right">{item.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top Features */}
          {topFeatures.length > 0 && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5">
              <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">En Çok Kullanılan Özellikler</h2>
              <div className="space-y-2">
                {topFeatures.slice(0, 8).map((f: any, i: number) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{f.feature || f.feature_name || f}</span>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                      {f.count || f.usage_count || 0}x
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity by Type */}
          {Object.keys(activityByType).length > 0 && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5">
              <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Aktivite Türleri (30 Gün)</h2>
              <div className="space-y-2">
                {Object.entries(activityByType)
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .slice(0, 8)
                  .map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{type}</span>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                        {count as number}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Daily Activity (last 30 days mini chart) */}
        {dailyActivity.length > 0 && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5 mb-8">
            <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Günlük Aktivite (30 Gün)</h2>
            <div className="flex items-end gap-0.5 h-16">
              {dailyActivity.slice(-30).map((day, i) => {
                const max = Math.max(...dailyActivity.map((d) => d.count), 1);
                const h = Math.max((day.count / max) * 100, 4);
                return (
                  <div
                    key={i}
                    className="flex-1 bg-gray-700 dark:bg-gray-400 rounded-t hover:bg-gray-900 dark:hover:bg-white transition-colors"
                    style={{ height: `${h}%` }}
                    title={`${day.date}: ${day.count} işlem`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-gray-400">
                {dailyActivity[0]?.date ? new Date(dailyActivity[0].date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : ''}
              </span>
              <span className="text-[10px] text-gray-400">
                {dailyActivity[dailyActivity.length - 1]?.date ? new Date(dailyActivity[dailyActivity.length - 1].date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : ''}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5 mb-8">
          <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">İşlemler</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={tokensToAdd || ''}
                onChange={(e) => setTokensToAdd(parseInt(e.target.value) || 0)}
                placeholder="Token sayısı"
                className="w-28 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <button
                onClick={handleAddTokens}
                disabled={saving || tokensToAdd <= 0}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Token Ekle
              </button>
            </div>
            {!isAdmin && (
              <button
                onClick={handleToggleTester}
                disabled={saving}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  isTester
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {isTester ? '✓ Test Kullanıcısı' : 'Test Kullanıcısı Yap'}
              </button>
            )}
          </div>
        </div>

        {/* Token History */}
        {tokenHistory.length > 0 && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5">
            <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Token Geçmişi</h2>
            <div className="space-y-1">
              {tokenHistory.slice(0, 30).map((tx: any, i: number) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white">{tx.reason || tx.transaction_type || 'İşlem'}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString('tr-TR') : ''}
                    </p>
                  </div>
                  <span className={`text-sm font-medium ${tx.amount > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetailPage;
